import { font } from "@/assets/fonts/Poppins-Regular-normal.js";
import { bold } from "@/assets/fonts/Poppins-bold.js";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

import { STATIC_ORDER_LOGO_PATH } from "@/common/constants";
import { deliveryOptionsCodes } from "@/common/fields/delivery-options";
import { paymentMethodsCodes } from "@/common/fields/orders-payment-methods";
import { paymentStatusesCodes } from "@/common/fields/orders-payment-status-codes";
import { ordersStatusesCodes } from "@/common/fields/orders-status-codes";

import { formatDate, getNowFormatted } from "./date";
import { elementToImage } from "./images";
import { formatCurrency } from "./money";
import { cutString } from "./transformers";

// Si el navegador bloquea la pestaña nueva (pasa cuando se abre despues de
// un await), se descarga el archivo en vez de no hacer nada.
export const openPDF = (pdf, filename = "documento") => {
    const blob = pdf.output("blob");
    const url = URL.createObjectURL(blob);
    const opened = window.open(url, "_blank");
    if (!opened) {
        pdf.save(`${filename}.pdf`);
    }
};

export const downLoadPDF = (pdf, filename) => {
    pdf.save(`${filename}.pdf`);
};

export const HALF_LETTER_FORMAT_MM = [139.7, 215.9];

// Sin `format` el PDF toma el tamaño exacto de la captura (tiquete). Con
// `format` (en mm) la captura se escala para caber en la hoja con `margin`.
export const elementToPDF = async (element, { format, margin = 0 } = {}) => {
    const imgData = await elementToImage(element);
    if (!format) {
        const doc = new jsPDF({
            orientation: "p",
            unit: "pt",
            format: [imgData.width, imgData.height]
        });
        const imgProps = doc.getImageProperties(imgData.image);
        const pdfWidth = doc.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        doc.addImage(imgData.image, "PNG", 0, 0, pdfWidth, pdfHeight);
        return doc;
    }
    const doc = new jsPDF({ orientation: "p", unit: "mm", format });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const scale = Math.min(
        (pageWidth - margin * 2) / imgData.width,
        (pageHeight - margin * 2) / imgData.height
    );
    const width = imgData.width * scale;
    const height = imgData.height * scale;
    doc.addImage(
        imgData.image,
        "PNG",
        (pageWidth - width) / 2,
        margin,
        width,
        height
    );
    return doc;
};

const addFilterOrders = (query, doc, startY) => {
    let currentY = startY;
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);

    let filterTexts = [];
    if (query.createdAt && (query.createdAt.begin || query.createdAt.end)) {
        const dateRange = `Rango: ${query.createdAt.begin || ""} | ${
            query.createdAt.end || ""
        }`;
        doc.setFont("Poppins", "bold");
        doc.text(`Fecha creación:`, 15, currentY);
        doc.setFont("Poppins", "normal");
        doc.text(dateRange, 32, currentY);
        currentY += 3;
    }

    if (query.deliveryStatus) {
        const status =
            ordersStatusesCodes[query.deliveryStatus] || query.deliveryStatus;
        doc.setFont("Poppins", "bold");
        doc.text(`Estado:`, 15, currentY);
        doc.setFont("Poppins", "normal");
        doc.text(`${status}`, 32, currentY);
        currentY += 3;
    }

    if (filterTexts.length > 0) {
        currentY += 3;
        doc.setFont("Poppins", "bold");
        doc.setFontSize(11);

        filterTexts.forEach((text) => {
            doc.text(`${text}`, 15, currentY);
            currentY += 3;
        });
    }

    doc.setTextColor(0, 0, 0);
    return currentY;
};

const DARK_GRAY = [50, 50, 50];
const LIGHT_GRAY = [245, 245, 245];
const BORDER_GRAY = [220, 220, 220];

const drawOrderLogo = (doc, logoUrl, x, y, width) => {
    const draw = (url) => {
        const imgProps = doc.getImageProperties(url);
        const height = (imgProps.height * width) / imgProps.width;
        doc.addImage(url, x, y, width, height);
    };
    try {
        draw(logoUrl || STATIC_ORDER_LOGO_PATH);
    } catch {
        try {
            draw(STATIC_ORDER_LOGO_PATH);
        } catch {
            // sin logo disponible, se continúa con el resto del PDF
        }
    }
};

const addCustomerNoteBox = (doc, customerNote, startY, pageWidth) => {
    const marginX = 10;
    const boxWidth = pageWidth - marginX * 2;
    const textMaxWidth = boxWidth - 12;
    doc.setFont("Poppins", "normal");
    doc.setFontSize(9);
    const noteLines = doc.splitTextToSize(customerNote, textMaxWidth);
    const lineHeight = 5;
    const boxHeight = lineHeight * (noteLines.length + 1) + 6;

    doc.setFillColor(...LIGHT_GRAY);
    doc.rect(marginX, startY, boxWidth, boxHeight, "F");
    doc.setFillColor(...DARK_GRAY);
    doc.rect(marginX, startY, 2, boxHeight, "F");

    let textY = startY + 6;
    doc.setFont("Poppins", "bold");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text("Comentarios:", marginX + 6, textY);
    textY += lineHeight;

    doc.setFont("Poppins", "normal");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(noteLines, marginX + 6, textY);

    return startY + boxHeight + 10;
};

export const generateOrderPDF = async (order) => {
    const doc = new jsPDF({
        format: "letter",
        unit: "mm"
    });
    doc.addFileToVFS("Poppins-Regular-normal.ttf", font);
    doc.addFileToVFS("Poppins-bold.ttf", bold);
    doc.addFont("Poppins-Regular-normal.ttf", "Poppins", "normal");
    doc.addFont("Poppins-bold.ttf", "Poppins", "bold");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFont("Poppins", "bold");
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text(`${order.orderMessage} #${order.id} | ${order.uuid}`, 10, 20);

    if (order.tenantName) {
        doc.setFont("Poppins", "normal");
        doc.setFontSize(11);
        doc.text(order.tenantName, 10, 26);
    }

    const logoWidth = 30;
    drawOrderLogo(doc, order.logoUrl, pageWidth - 15 - logoWidth, 8, logoWidth);

    doc.setDrawColor(...DARK_GRAY);
    doc.setLineWidth(0.8);
    doc.line(10, 30, pageWidth - 10, 30);

    const leftDetails = [
        { label: "Fecha:", value: formatDate(order.createdAt) }
    ];
    if (order.customer) {
        leftDetails.push({
            label: "Cliente:",
            value: `${order.customer.firstName} ${order.customer.lastName}`
        });
    }
    if (order.approvedPaymentMethodMessage) {
        leftDetails.push({
            label: "Método de pago:",
            value: order.approvedPaymentMethodMessage
        });
    }
    leftDetails.push({
        label: "Fecha de entrega:",
        value: formatDate(order.deliveryAt)
    });

    const rightDetails = [];
    if (order.supplyMerchant) {
        const merchantInfo = [order.supplyMerchant.name];
        if (
            order.supplyMerchant.identificationNumber &&
            order.supplyMerchant.identificationType
        ) {
            merchantInfo.push(
                `${order.supplyMerchant.identificationType}: ${order.supplyMerchant.identificationNumber}`
            );
        }
        if (order.supplyMerchant.address) {
            merchantInfo.push(order.supplyMerchant.address);
        }
        rightDetails.push({
            label: "Proveedor:",
            value: merchantInfo.join(" | ")
        });
    }
    if (order.pickupMerchant) {
        rightDetails.push({
            label: "Punto:",
            value: `${order.pickupMerchant.name} | ${order.pickupMerchant.address}`
        });
    }
    if (order.location) {
        rightDetails.push({
            label: "Domicilio:",
            value: `${order.location.address} | ${order.location.addressComplement}`
        });
    }

    const leftColumnX = 10;
    const leftLabelWidth = 42;
    const rightColumnX = pageWidth / 2 + 5;
    const rightLabelWidth = 26;
    const detailsStartY = 38;
    doc.setFontSize(10);

    let leftY = detailsStartY;
    leftDetails.forEach(({ label, value }) => {
        doc.setFont("Poppins", "bold");
        doc.text(label, leftColumnX, leftY);
        doc.setFont("Poppins", "normal");
        doc.text(
            doc.splitTextToSize(
                value,
                pageWidth / 2 - leftColumnX - leftLabelWidth
            ),
            leftColumnX + leftLabelWidth,
            leftY
        );
        leftY += 6;
    });

    let rightY = detailsStartY;
    rightDetails.forEach(({ label, value }) => {
        doc.setFont("Poppins", "bold");
        doc.text(label, rightColumnX, rightY);
        doc.setFont("Poppins", "normal");
        doc.text(
            doc.splitTextToSize(
                value,
                pageWidth - rightColumnX - rightLabelWidth - 10
            ),
            rightColumnX + rightLabelWidth,
            rightY
        );
        rightY += 6;
    });

    const detailsEndY = Math.max(leftY, rightY);

    const filteredReferences = order.references.filter((ref) => !!ref.quantity);
    const hasSize = filteredReferences.some((ref) => !!ref.size);
    const hasColor = filteredReferences.some((ref) => !!ref.color);
    const extraColumnsCount = (hasSize ? 1 : 0) + (hasColor ? 1 : 0);
    const productWidth =
        extraColumnsCount > 1 ? 70 - (extraColumnsCount - 1) * 22 : 70;

    const head = ["Producto", "Cantidad"];
    if (hasSize) head.push("Tamaño");
    if (hasColor) head.push("Color");
    head.push("Precio Unitario", "Total");

    const body = filteredReferences.map((ref) => {
        const row = [ref.product.name, ref.quantity];
        if (hasSize) row.push(ref.size || "N/A");
        if (hasColor) row.push(ref.color || "N/A");
        row.push(ref.priceApplied || "", ref.totalPriceApplied || "");
        return row;
    });

    const columnStyles = { 0: { cellWidth: productWidth } };
    let columnIndex = 1;
    columnStyles[columnIndex] = { cellWidth: 22, halign: "center" };
    columnIndex += 1;
    if (hasSize) {
        columnStyles[columnIndex] = { cellWidth: 22, halign: "center" };
        columnIndex += 1;
    }
    if (hasColor) {
        columnStyles[columnIndex] = { cellWidth: 22, halign: "center" };
        columnIndex += 1;
    }
    columnStyles[columnIndex] = { cellWidth: 35, halign: "right" };
    columnIndex += 1;
    columnStyles[columnIndex] = { cellWidth: 35, halign: "right" };

    doc.autoTable({
        head: [head],
        body,
        startY: detailsEndY + 6,
        theme: "grid",
        margin: 10,
        columnStyles,
        headStyles: {
            fillColor: DARK_GRAY,
            textColor: [255, 255, 255],
            fontSize: 10,
            fontStyle: "bold"
        },
        bodyStyles: {
            fontSize: 10
        },
        styles: {
            font: "Poppins",
            overflow: "linebreak"
        }
    });
    const finalY = doc.previousAutoTable.finalY || detailsEndY;
    let currentYPoint = finalY + 12;

    if (order.customerNote) {
        currentYPoint = addCustomerNoteBox(
            doc,
            order.customerNote,
            currentYPoint,
            pageWidth
        );
    }

    if (order.hasDeliveryPrice) {
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.setFont("Poppins", "bold");
        doc.text("Valor productos:", pageWidth - 70, currentYPoint);
        doc.setFont("Poppins", "normal");
        doc.text(
            order.referencesValueWithDiscount,
            pageWidth - 50,
            currentYPoint
        );
        currentYPoint += 16;
        doc.setFont("Poppins", "bold");
        doc.text("Costo Domicilio:", pageWidth - 70, currentYPoint);
        doc.setFont("Poppins", "normal");
        doc.text(order.deliveryPrice, pageWidth - 50, currentYPoint);
        currentYPoint += 16;
    }
    doc.setFontSize(18);
    doc.setFont("Poppins", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("Total:", pageWidth - 70, currentYPoint);
    doc.setFont("Poppins", "normal");
    doc.text(order.total, pageWidth - 50, currentYPoint);

    if (order.hasBalance) {
        if (currentYPoint + 30 > pageHeight - 25) {
            doc.addPage();
            currentYPoint = 20;
        } else {
            currentYPoint += 12;
        }
        doc.setFontSize(14);
        doc.setFont("Poppins", "bold");
        doc.text("Abono:", pageWidth - 70, currentYPoint);
        doc.setFont("Poppins", "normal");
        doc.text(order.valuePaid, pageWidth - 50, currentYPoint);
        if (order.paymentMethodMessage) {
            currentYPoint += 6;
            doc.setFontSize(9);
            doc.text(
                `Pagado con ${order.paymentMethodMessage}`,
                pageWidth - 50,
                currentYPoint
            );
        }
        currentYPoint += 10;
        doc.setFontSize(14);
        doc.setFont("Poppins", "bold");
        doc.text("Saldo:", pageWidth - 70, currentYPoint);
        doc.setFont("Poppins", "normal");
        doc.text(order.balance, pageWidth - 50, currentYPoint);
    }
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(8);
    doc.setFont("Poppins", "normal");
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setDrawColor(...BORDER_GRAY);
        doc.setLineWidth(0.2);
        doc.line(10, pageHeight - 20, pageWidth - 10, pageHeight - 20);
        doc.text(
            `Generado por POS tucatalogo.co | Página ${i} de ${totalPages}`,
            pageWidth / 2,
            pageHeight - 16,
            { align: "center" }
        );
    }
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
};

export const generateProductsListPDF = async (products) => {
    const doc = new jsPDF({
        format: "letter",
        unit: "mm",
        orientation: "landscape"
    });

    doc.addFileToVFS("Poppins-Regular-normal.ttf", font);
    doc.addFileToVFS("Poppins-bold.ttf", bold);
    doc.addFont("Poppins-Regular-normal.ttf", "Poppins", "normal");
    doc.addFont("Poppins-bold.ttf", "Poppins", "bold");

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.addImage(
        "/img/logos/complete_logo.png",
        "PNG",
        pageWidth - 50,
        10,
        30,
        19
    );

    doc.setFont("Poppins", "bold");
    doc.setFontSize(18);
    doc.text("Listado de Productos Seleccionados", 15, 20);

    doc.setFont("Poppins", "normal");
    doc.setFontSize(10);
    const today = getNowFormatted();
    doc.text(`Fecha: ${today}`, 15, 28);
    doc.text(`Total productos: ${products.length}`, 15, 34);

    doc.autoTable({
        head: [["SKU", "Cod.", "Nombre", "Talla", "Color", "Precio", "Stock"]],
        body: products.flatMap((product) =>
            product.references.map((ref) => [
                product.sku,
                ref.barcode,
                cutString(product.name || "Sin nombre", 60),
                ref.size,
                ref.color,
                formatCurrency(ref.priceOffer),
                ref.stock
            ])
        ),
        startY: 40,
        theme: "grid",
        margin: { left: 15, right: 15 },
        headStyles: {
            fillColor: [0, 0, 0],
            textColor: [255, 255, 255],
            fontSize: 9,
            fontStyle: "bold",
            halign: "left"
        },
        bodyStyles: {
            fontSize: 8,
            cellPadding: 3
        },
        columnStyles: {
            0: { cellWidth: 25 },
            1: { cellWidth: 25 },
            2: { cellWidth: 70 },
            3: { cellWidth: 30 },
            4: { cellWidth: 25 },
            5: { cellWidth: 35, halign: "right" },
            6: { cellWidth: 20 }
        },
        styles: {
            font: "Poppins",
            overflow: "linebreak"
        }
    });

    doc.setFontSize(8);
    doc.setFont("Poppins", "normal");
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.text(
            `Generado por POS tucatalogo.co | Página ${i} de ${totalPages}`,
            pageWidth / 2,
            pageHeight - 10,
            {
                align: "center"
            }
        );
    }

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
};

export const generateProductsListCompletePDF = async (products) => {
    const doc = new jsPDF({
        format: "letter",
        unit: "mm",
        orientation: "landscape"
    });

    doc.addFileToVFS("Poppins-Regular-normal.ttf", font);
    doc.addFileToVFS("Poppins-bold.ttf", bold);
    doc.addFont("Poppins-Regular-normal.ttf", "Poppins", "normal");
    doc.addFont("Poppins-bold.ttf", "Poppins", "bold");

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.addImage(
        "/img/logos/complete_logo.png",
        "PNG",
        pageWidth - 50,
        10,
        30,
        19
    );

    doc.setFont("Poppins", "bold");
    doc.setFontSize(18);
    doc.text("Listado de Productos y Variantes", 15, 20);

    doc.setFont("Poppins", "normal");
    doc.setFontSize(10);
    const today = getNowFormatted();
    doc.text(`Fecha: ${today}`, 15, 28);
    doc.text(`Total productos: ${products.length}`, 15, 34);

    let currentY = 45;

    products.forEach((product, index) => {
        if (currentY > pageHeight - 60) {
            doc.addPage();
            currentY = 20;
        }

        doc.setFont("Poppins", "bold");
        doc.setFontSize(12);
        doc.text(
            `Producto ${index + 1}: ${cutString(
                product.name || "Sin nombre",
                80
            )}`,
            15,
            currentY
        );
        currentY += 7;

        doc.setFont("Poppins", "normal");
        doc.setFontSize(9);

        const productInfo = [
            `SKU: ${product.sku || "NA / ID: " + product.id}`,
            `Descripción: ${cutString(
                product.summary || "Sin descripción",
                120
            )}`,
            `Precio anterior: ${formatCurrency(product.price)}`,
            `Precio de venta: ${formatCurrency(product.priceOffer)}`,
            `Marca: ${product.brandSlug || "N/A"}`,
            `Categoría: ${product.lineSlug || "N/A"}`
        ];

        productInfo.forEach((info) => {
            doc.text(info, 20, currentY);
            currentY += 5;
        });

        currentY += 3;

        if (product.references && product.references.length > 0) {
            doc.autoTable({
                head: [
                    [
                        "ID Ref",
                        "Código Barras",
                        "Talla",
                        "Color",
                        "Precio anterior",
                        "Precio de venta",
                        "Stock"
                    ]
                ],
                body: product.references.map((ref) => [
                    ref.id || "N/A",
                    ref.barcode || "N/A",
                    ref.size || "N/A",
                    ref.color || "N/A",
                    formatCurrency(ref.price),
                    formatCurrency(ref.priceOffer),
                    ref.stock !== undefined ? ref.stock : "N/A"
                ]),
                startY: currentY,
                theme: "grid",
                margin: { left: 20, right: 15 },
                headStyles: {
                    fillColor: [52, 73, 94],
                    textColor: [255, 255, 255],
                    fontSize: 8,
                    fontStyle: "bold",
                    halign: "center"
                },
                bodyStyles: {
                    fontSize: 7,
                    cellPadding: 2
                },
                columnStyles: {
                    0: { cellWidth: 20, halign: "center" },
                    1: { cellWidth: 35, halign: "center" },
                    2: { cellWidth: 20, halign: "center" },
                    3: { cellWidth: 25, halign: "center" },
                    4: { cellWidth: 30, halign: "right" },
                    5: { cellWidth: 30, halign: "right" },
                    6: { cellWidth: 20, halign: "center" }
                },
                styles: {
                    font: "Poppins",
                    overflow: "linebreak"
                }
            });

            currentY = doc.previousAutoTable.finalY + 10;
        } else {
            doc.setFont("Poppins", "normal");
            doc.setFontSize(8);
            doc.text(
                "No hay referencias asociadas a este producto",
                20,
                currentY
            );
            currentY += 10;
        }
    });

    doc.setFontSize(8);
    doc.setFont("Poppins", "normal");
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.text(
            `Generado por POS tucatalogo.co | Página ${i} de ${totalPages}`,
            pageWidth / 2,
            pageHeight - 10,
            {
                align: "center"
            }
        );
    }

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
};

export const generateReferenceOrdersListPDF = async (references, query) => {
    const doc = new jsPDF({
        format: "letter",
        unit: "mm",
        orientation: "landscape"
    });

    doc.addFileToVFS("Poppins-Regular-normal.ttf", font);
    doc.addFileToVFS("Poppins-bold.ttf", bold);
    doc.addFont("Poppins-Regular-normal.ttf", "Poppins", "normal");
    doc.addFont("Poppins-bold.ttf", "Poppins", "bold");

    const pageWidth = doc.internal.pageSize.getWidth();

    doc.addImage(
        "/img/logos/complete_logo.png",
        "PNG",
        pageWidth - 50,
        10,
        30,
        19
    );

    doc.setFont("Poppins", "bold");
    doc.setFontSize(18);
    doc.text("Variantes en Ordenes", 15, 20);

    let filtersY = 26;
    let currentY = addFilterOrders(query, doc, filtersY);

    doc.setFont("Poppins", "normal");
    doc.setFontSize(10);
    const today = getNowFormatted();

    doc.text(`Fecha: ${today}`, 15, currentY);
    currentY += 6;
    doc.text(`Total referencias: ${references.length}`, 15, currentY);

    const tableStartY = currentY + 7;

    doc.autoTable({
        head: [
            [
                "SKU",
                "Cod.",
                "Nombre",
                "Talla",
                "Color",
                "Unidades",
                "# Ordenes",
                "Precio Actual"
            ]
        ],
        body: references.map((product) => [
            product.sku,
            product.barcode,
            cutString(product.name || "Sin nombre", 60),
            product.size,
            product.color,
            product.quantity,
            product.ordersLen,
            formatCurrency(product.priceOffer)
        ]),
        startY: tableStartY,
        theme: "grid",
        margin: { left: 15, right: 15 },
        headStyles: {
            fillColor: [50, 50, 50],
            textColor: [255, 255, 255],
            fontSize: 10,
            fontStyle: "bold",
            halign: "left"
        },
        bodyStyles: {
            fontSize: 8,
            cellPadding: 3
        },
        columnStyles: {
            0: { cellWidth: 25 },
            1: { cellWidth: 25 },
            2: { cellWidth: "auto" },
            3: { cellWidth: 20 },
            4: { cellWidth: 25 },
            5: { cellWidth: 20, halign: "center" },
            6: { cellWidth: 20, halign: "center" },
            7: { cellWidth: 35, halign: "right" }
        },
        styles: {
            font: "Poppins",
            overflow: "linebreak"
        }
    });

    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.setFont("Poppins", "normal");
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.text(
            `Generado por POS tucatalogo.co | Página ${i} de ${totalPages}`,
            pageWidth / 2,
            pageHeight - 10,
            {
                align: "center"
            }
        );
    }

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
};

export const generateReferenceOrdersListCompletePDF = async (
    references,
    query
) => {
    const doc = new jsPDF({
        format: "letter",
        unit: "mm",
        orientation: "landscape"
    });

    doc.addFileToVFS("Poppins-Regular-normal.ttf", font);
    doc.addFileToVFS("Poppins-bold.ttf", bold);
    doc.addFont("Poppins-Regular-normal.ttf", "Poppins", "normal");
    doc.addFont("Poppins-bold.ttf", "Poppins", "bold");

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.addImage(
        "/img/logos/complete_logo.png",
        "PNG",
        pageWidth - 50,
        10,
        30,
        19
    );

    doc.setFont("Poppins", "bold");
    doc.setFontSize(18);
    doc.text("Variantes en Ordenes", 15, 20);
    doc.setFontSize(12);

    doc.text(`Fecha de consulta:`, 15, 26);
    doc.setFont("Poppins", "normal");
    const today = getNowFormatted();
    doc.text(` ${today}`, 56, 26);

    doc.setFont("Poppins", "bold");
    doc.text(`Total referencias:`, 15, 32);
    doc.setFont("Poppins", "normal");
    doc.text(`${references.length}`, 56, 32);

    let filtersY = 38;
    let currentY = addFilterOrders(query, doc, filtersY);

    doc.setFont("Poppins", "bold");
    doc.setFontSize(11);

    currentY += 10;

    references.forEach((reference) => {
        if (currentY > pageHeight - 60) {
            doc.addPage();
            currentY = 20;
        }

        doc.setFont("Poppins", "bold");
        doc.setFontSize(11);
        doc.text(`Variante ID. ${reference.id}`, 15, currentY);
        currentY += 7;

        doc.setFont("Poppins", "normal");
        doc.setFontSize(10);

        const referenceInfo = [
            `Producto: ${cutString(reference.name || "N/A", 80)}`,
            `SKU: ${reference.sku || "N/A"}`,
            `Talla: ${reference.size || "N/A"}`,
            `Color: ${reference.color || "N/A"}`,
            `Precio: ${formatCurrency(reference.priceOffer)}`,
            `Cantidad total: ${reference.quantity || 0}`,
            `Código de barras: ${reference.barcode || "N/A"}`
        ];

        referenceInfo.forEach((info) => {
            doc.text(info, 20, currentY);
            currentY += 5;
        });

        currentY += 3;

        if (reference.orders && reference.orders.length > 0) {
            doc.autoTable({
                head: [
                    [
                        "ID",
                        "UUID",
                        "Unid.",
                        "Creación",
                        "Entrega",
                        "Estado Entrega",
                        "Estado Pago",
                        "Cliente",
                        "Total",
                        "Pagado"
                    ]
                ],
                body: reference.orders.map((order) => [
                    order.id || "N/A",
                    order.uuid || "N/A",
                    order.quantity,
                    formatDate(order.createdAt),
                    formatDate(order.deliveryAt),
                    ordersStatusesCodes[order.deliveryStatus] ||
                        order.deliveryStatus ||
                        "N/A",
                    paymentStatusesCodes[order.paymentStatus] ||
                        order.paymentStatus ||
                        "N/A",
                    order.customer
                        ? `${order.customer.firstName} ${order.customer.lastName}
${order.customer.phone}`
                        : "Sin cliente",
                    formatCurrency(order.total),
                    formatCurrency(order.valuePaid)
                ]),
                startY: currentY,
                theme: "grid",
                margin: { left: 10, right: 10 },
                styles: {
                    font: "Poppins",
                    fontSize: 8,
                    overflow: "linebreak",
                    cellPadding: 2
                },
                headStyles: {
                    fillColor: [50, 50, 50],
                    textColor: [255, 255, 255],
                    fontStyle: "bold",
                    halign: "center"
                },
                columnStyles: {
                    0: { cellWidth: 9, halign: "center" },
                    1: { cellWidth: 24 }, // UUID
                    2: { cellWidth: 12, halign: "center" }, // Unidades
                    3: { cellWidth: "auto" }, // Fecha Creación
                    4: { cellWidth: "auto" }, // Fecha Entrega
                    5: { cellWidth: "wrap" }, // Estado Entrega
                    6: { cellWidth: "wrap" }, // Estado Pago
                    7: { cellWidth: "auto" }, // Cliente
                    8: { cellWidth: 18, halign: "right" }, // Total
                    9: { cellWidth: 18, halign: "right" } // Pagado
                }
            });

            currentY = doc.previousAutoTable.finalY + 10;
        } else {
            doc.setFont("Poppins", "normal");
            doc.setFontSize(8);
            doc.text(
                "No hay órdenes asociadas a esta referencia",
                20,
                currentY
            );
            currentY += 10;
        }
    });

    doc.setFontSize(8);
    doc.setFont("Poppins", "normal");
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.text(
            `Generado por POS tucatalogo.co | Página ${i} de ${totalPages}`,
            pageWidth / 2,
            pageHeight - 10,
            {
                align: "center"
            }
        );
    }

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
};

export const generateOrdersListPDF = async (orders, query) => {
    const doc = new jsPDF({
        format: "letter",
        unit: "mm",
        orientation: "landscape"
    });

    doc.addFileToVFS("Poppins-Regular-normal.ttf", font);
    doc.addFileToVFS("Poppins-bold.ttf", bold);
    doc.addFont("Poppins-Regular-normal.ttf", "Poppins", "normal");
    doc.addFont("Poppins-bold.ttf", "Poppins", "bold");

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.addImage(
        "/img/logos/complete_logo.png",
        "PNG",
        pageWidth - 50,
        10,
        30,
        19
    );

    doc.setFont("Poppins", "bold");
    doc.setFontSize(18);
    doc.text("Listado de Ordenes", 15, 20);

    doc.setFont("Poppins", "normal");
    doc.setFontSize(12);
    const today = getNowFormatted();
    doc.text(`Fecha: ${today}`, 15, 28);
    doc.text(`Total ordenes: ${orders.length}`, 15, 34);
    let startY = 40;
    let currentY = addFilterOrders(query, doc, startY);

    doc.autoTable({
        head: [
            [
                "ID",
                "Estado",
                "Estado Pago",
                "Opción Entrega",
                "Método Pago",
                "Cliente",
                "Total"
            ]
        ],
        body: orders.map((order) => [
            order.id || "N/A",
            cutString(ordersStatusesCodes[order.deliveryStatus] || "N/A", 30),
            cutString(paymentStatusesCodes[order.paymentStatus] || "N/A", 30),
            cutString(deliveryOptionsCodes[order.deliveryOption] || "N/A", 30),
            cutString(paymentMethodsCodes[order.paymentMethod] || "N/A", 30),
            order.customer
                ? cutString(
                      `${order.customer.firstName} ${order.customer.lastName}`,
                      40
                  )
                : "Sin cliente",
            formatCurrency(order.total)
        ]),
        startY: currentY,
        theme: "grid",
        margin: { left: 15, right: 15 },
        headStyles: {
            fillColor: [50, 50, 50],
            textColor: [255, 255, 255],
            fontSize: 10,
            fontStyle: "bold",
            halign: "left"
        },
        bodyStyles: {
            fontSize: 9,
            cellPadding: 3
        },
        columnStyles: {
            0: { cellWidth: 12, halign: "center" },
            1: { cellWidth: 35 },
            2: { cellWidth: 35 },
            3: { cellWidth: 35 },
            4: { cellWidth: 35 },
            5: { cellWidth: 45 },
            6: { cellWidth: 30, halign: "right" }
        },
        styles: {
            font: "Poppins",
            overflow: "linebreak"
        }
    });

    doc.setFontSize(8);
    doc.setFont("Poppins", "normal");
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.text(
            `Generado por POS tucatalogo.co | Página ${i} de ${totalPages}`,
            pageWidth / 2,
            pageHeight - 10,
            {
                align: "center"
            }
        );
    }

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
};

export const generateOrderDetailedListPDF = async (orders, selectedDate) => {
    const doc = new jsPDF({
        format: "letter",
        unit: "mm",
        orientation: "landscape"
    });

    doc.addFileToVFS("Poppins-Regular-normal.ttf", font);
    doc.addFileToVFS("Poppins-bold.ttf", bold);
    doc.addFont("Poppins-Regular-normal.ttf", "Poppins", "normal");
    doc.addFont("Poppins-bold.ttf", "Poppins", "bold");

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.addImage(
        "/img/logos/complete_logo.png",
        "PNG",
        pageWidth - 50,
        10,
        30,
        19
    );

    doc.setFont("Poppins", "bold");
    doc.setFontSize(18);
    const displayDate = selectedDate || "fecha no especificada";
    doc.text(`Ordenes para entrega el ${displayDate}`, 15, 20);

    doc.setFont("Poppins", "normal");
    doc.setFontSize(10);
    const today = getNowFormatted();
    doc.text(`Fecha: ${today}`, 15, 28);
    doc.text(`Total ordenes: ${orders.length}`, 15, 34);

    let currentY = 45;

    orders.forEach((order, index) => {
        if (currentY > pageHeight - 80) {
            doc.addPage();
            currentY = 20;
        }

        doc.setFont("Poppins", "bold");
        doc.setFontSize(12);
        doc.text(`Orden #${order.id}`, 15, currentY);
        currentY += 7;

        doc.setFont("Poppins", "normal");
        doc.setFontSize(9);

        const orderInfo = [
            `Fecha creación: ${formatDate(order.createdAt)}`,
            `Fecha entrega: ${formatDate(order.deliveryAt)}`,
            `Estado entrega: ${
                ordersStatusesCodes[order.deliveryStatus] ||
                order.deliveryStatus ||
                "N/A"
            }`,
            `Estado pago: ${
                paymentStatusesCodes[order.paymentStatus] ||
                order.paymentStatus ||
                "N/A"
            }`,
            `Opción entrega: ${
                deliveryOptionsCodes[order.deliveryOption] || "N/A"
            }`,
            `Método de pago: ${
                paymentMethodsCodes[order.paymentMethod] || "N/A"
            }`
        ];

        if (order.customer) {
            orderInfo.push(
                `Cliente: ${order.customer.firstName} ${order.customer.lastName} | Tel: ${order.customer.phone}`
            );
        } else {
            orderInfo.push("Cliente: Sin cliente asignado");
        }

        orderInfo.push(`Total: ${formatCurrency(order.total)}`);
        orderInfo.push(`Pagado: ${formatCurrency(order.valuePaid)}`);
        orderInfo.push(
            `Saldo: ${formatCurrency(order.total - order.valuePaid)}`
        );

        orderInfo.forEach((info) => {
            doc.text(info, 20, currentY);
            currentY += 5;
        });

        currentY += 4;

        if (order.references && order.references.length > 0) {
            doc.autoTable({
                head: [
                    [
                        "Cod.",
                        "Producto",
                        "Talla",
                        "Color",
                        "Precio Unit.",
                        "Cantidad",
                        "Descuento",
                        "Total"
                    ]
                ],
                body: order.references
                    .filter((ref) => !!ref.quantity)
                    .map((ref) => [
                        ref.barcode || "N/A",
                        cutString(ref.product?.name || "Sin nombre", 50),
                        ref.size || "N/A",
                        ref.color || "N/A",
                        formatCurrency(ref.priceOffer || ref.price),
                        ref.quantity,
                        formatCurrency(ref.appliedDiscountPerReference || 0),
                        formatCurrency(
                            (ref.priceOffer || ref.price) * ref.quantity -
                                (ref.appliedDiscountPerReference || 0)
                        )
                    ]),
                startY: currentY,
                theme: "grid",
                margin: { left: 10, right: 10 },
                headStyles: {
                    fillColor: [50, 50, 50],
                    textColor: [255, 255, 255],
                    fontSize: 12,
                    fontStyle: "bold",
                    halign: "center"
                },
                bodyStyles: {
                    fontSize: 10,
                    cellPadding: 3
                },
                columnStyles: {
                    0: { cellWidth: 22, halign: "center" },
                    1: { cellWidth: "auto", halign: "left" },
                    2: { cellWidth: 20, halign: "center" },
                    3: { cellWidth: 20, halign: "center" },
                    4: { cellWidth: 25, halign: "right" },
                    5: { cellWidth: 24, halign: "center" },
                    6: { cellWidth: 28, halign: "right" },
                    7: { cellWidth: 26, halign: "right" }
                },
                styles: {
                    font: "Poppins",
                    overflow: "linebreak"
                }
            });

            currentY = doc.previousAutoTable.finalY + 10;
        } else {
            doc.setFont("Poppins", "normal");
            doc.setFontSize(8);
            doc.text("No hay productos en esta orden", 20, currentY);
            currentY += 10;
        }

        if (index < orders.length - 1) {
            currentY += 5;
        }
    });

    doc.setFontSize(8);
    doc.setFont("Poppins", "normal");
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.text(
            `Generado por POS tucatalogo.co | Página ${i} de ${totalPages}`,
            pageWidth / 2,
            pageHeight - 10,
            {
                align: "center"
            }
        );
    }

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
};

export const generateSalesByProductPDF = async (product, references) => {
    const doc = new jsPDF({
        format: "letter",
        unit: "mm",
        orientation: "landscape"
    });
    doc.addFileToVFS("Poppins-Regular-normal.ttf", font);
    doc.addFileToVFS("Poppins-bold.ttf", bold);
    doc.addFont("Poppins-Regular-normal.ttf", "Poppins", "normal");
    doc.addFont("Poppins-bold.ttf", "Poppins", "bold");

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.addImage(
        "/img/logos/complete_logo.png",
        "PNG",
        pageWidth - 45,
        10,
        30,
        15
    );

    doc.setFont("Poppins", "bold");
    doc.setFontSize(20);
    doc.text("Detalle de Ventas por Producto", 15, 25);

    doc.setFontSize(11);
    doc.text(`Producto: `, 15, 34);
    doc.setFont("Poppins", "normal");
    doc.text(`${product.name || "N/A"}`, 38, 34);

    doc.setFont("Poppins", "bold");
    doc.text(`SKU: `, 15, 38);
    doc.setFont("Poppins", "normal");
    doc.text(`${product.sku || "Sin SKU"}`, 28, 38);

    doc.setFontSize(10);
    doc.setFont("Poppins", "bold");
    doc.text(`Fecha de reporte:`, 15, 42);
    doc.setFont("Poppins", "normal");
    doc.text(`${getNowFormatted()}`, 47, 42);

    doc.setFont("Poppins", "bold");
    doc.text(`# Unidades totales vendidas: ${product.totalSales || 0}`, 15, 47); // Eliminé formatCurrency si son unidades
    doc.text(
        `Total en ventas: ${formatCurrency(product.totalRevenue)}`,
        15,
        51
    );

    doc.setFontSize(8);
    doc.setFont("Poppins", "normal");
    const note =
        "* El precio de la tabla es el precio actual del producto. Las ventas se calculan según el precio histórico y descuentos aplicados.";
    doc.text(note, 15, 56);

    const tableBody = references.map((item) => [
        item.id || "N/A",
        item.barcode || "Sin código",
        item.size?.toUpperCase() || "Único",
        item.color || "N/A",
        formatCurrency(item.priceOffer),
        item.sales || 0,
        formatCurrency(item.revenue)
    ]);

    doc.autoTable({
        startY: 60,
        head: [
            [
                "ID",
                "Código de Barras",
                "Talla",
                "Color",
                "Precio Unit.",
                "Unidades",
                "Ventas Totales"
            ]
        ],
        body: tableBody,
        theme: "grid",
        margin: { left: 10, right: 10 },

        headStyles: {
            fillColor: [50, 50, 50],
            textColor: [255, 255, 255],
            fontSize: 11,
            fontStyle: "bold",
            halign: "center",
            valign: "middle"
        },

        bodyStyles: {
            font: "Poppins",
            fontSize: 10,
            cellPadding: 3
        },

        styles: {
            overflow: "linebreak"
        },

        columnStyles: {
            0: { cellWidth: 15, halign: "center" },
            1: { cellWidth: "auto", halign: "left" },
            2: { cellWidth: 20, halign: "center" },
            3: { cellWidth: 25, halign: "center" },
            4: { cellWidth: 28, halign: "right" },
            5: { cellWidth: 22, halign: "center" },
            6: { cellWidth: 30, halign: "right" }
        }
    });

    const totalPages = doc.internal.getNumberOfPages();
    doc.setFontSize(8);
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.text(
            `Reporte de Producto: ${product.name} | Página ${i} de ${totalPages}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: "center" }
        );
    }

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
};
